/**
 * Reviews 页面原生 JS 控制器
 * 完全替代 Alpine.js，消除初始化时序问题
 * 使用面向对象（Class）方式，遵循 CLAUDE.md 规范
 */

(function() {
  if (window.__satellaiReviewsPageLoaded) return;
  window.__satellaiReviewsPageLoaded = true;

/* ===== 1. Tab 管理器 ===== */
class ReviewsTabManager {
  constructor() {
    this.activeTab = '';
    this.buttons = [];
    this.sections = [];
  }

  /**
   * 初始化 Tab 系统
   * 从 URL 参数或第一个 tab 确定初始 activeTab
   */
  init() {
    this.buttons = document.querySelectorAll('[data-tab-button]');
    this.sections = document.querySelectorAll('[data-tab-id]');

    if (this.buttons.length === 0) return;

    /* 从 URL 参数读取 tab */
    var params = new URLSearchParams(window.location.search);
    var urlTab = params.get('tab');
    if (urlTab) {
      var normalized = urlTab.toLowerCase().replace(/\s+/g, '-');
      var found = false;
      this.buttons.forEach(function(btn) {
        if (btn.dataset.tabButton === normalized) {
          found = true;
        }
      });
      if (found) {
        this.activeTab = normalized;
      }
    }

    /* 没有 URL 参数则使用第一个 tab */
    if (!this.activeTab && this.buttons.length > 0) {
      this.activeTab = this.buttons[0].dataset.tabButton;
    }

    /* 绑定按钮点击事件 */
    var self = this;
    this.buttons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        self.setTab(btn.dataset.tabButton);
      });
    });

    /* 初始化视图 */
    this.updateView();
  }

  /**
   * 切换到指定 Tab
   * @param {string} tabId - Tab ID
   */
  setTab(tabId) {
    this.activeTab = tabId;
    this.updateView();

    /* 更新 URL 参数 */
    var url = new URL(window.location);
    url.searchParams.set('tab', tabId);
    window.history.replaceState({}, '', url);
  }

  /** 更新按钮样式和 section 可见性 */
  updateView() {
    var activeTab = this.activeTab;

    /* 更新按钮样式 */
    this.buttons.forEach(function(btn) {
      if (btn.dataset.tabButton === activeTab) {
        btn.classList.remove('inactive');
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
        btn.classList.add('inactive');
      }
    });

    /* 更新 section 可见性 */
    this.sections.forEach(function(section) {
      var sectionTabId = section.dataset.tabId;
      if (!sectionTabId || sectionTabId === '') {
        /* 无 tab 关联 → 始终可见 */
        section.classList.remove('tab-hidden');
      } else if (sectionTabId === activeTab) {
        section.classList.remove('tab-hidden');
      } else {
        section.classList.add('tab-hidden');
      }
    });
  }
}

/* ===== 2. 懒加载管理器（fade-in 动画）===== */
class ReviewsLazyLoader {
  constructor() {
    this.observer = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          }
        }.bind(this));
      }.bind(this),
      { threshold: 0.3 }
    );
  }

  /** 观察一个元素，进入视口时添加 is-visible class */
  observe(el) {
    this.observer.observe(el);
  }

  /** 批量观察所有 [data-lazy-fade] 元素 */
  init() {
    var self = this;
    document.querySelectorAll('[data-lazy-fade]').forEach(function(el) {
      self.observe(el);
    });
  }
}

/* ===== 3. 视频懒加载 ===== */
class ReviewsVideoLoader {
  constructor() {
    this.observer = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var video = entry.target;
            video.load();
            video.play().catch(function() {});
            this.observer.unobserve(video);
          }
        }.bind(this));
      }.bind(this),
      { threshold: 0.5 }
    );
  }

  /** 批量观察所有 [data-lazy-video] 元素 */
  init() {
    var self = this;
    document.querySelectorAll('[data-lazy-video]').forEach(function(v) {
      self.observer.observe(v);
    });
  }
}

/* ===== 4. Grid 分页器 ===== */
class ReviewsGridPaginator {
  /**
   * @param {HTMLElement} sectionEl - 包含 data-reviews-grid 的 section 元素
   */
  constructor(sectionEl) {
    this.el = sectionEl;
    this.currentPage = 1;
    this.itemsPerPage = parseInt(sectionEl.dataset.perPage) || 9;
    this.cards = sectionEl.querySelectorAll('[data-grid-card]');
    this.totalPages = Math.ceil(this.cards.length / this.itemsPerPage);
    this.paginationEl = sectionEl.querySelector('[data-grid-pagination]');
  }

  /** 初始化分页器 */
  init() {
    if (this.cards.length === 0) return;
    this.renderPagination();
    this.updateView();
    this.bindEvents();
  }

  /**
   * 跳转到指定页码
   * @param {number} page - 页码（从1开始）
   */
  goToPage(page) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    /* 保存滚动位置，防止翻页时网格高度变化导致页面跳动 */
    var scrollY = window.scrollY;
    this.updateView();
    window.scrollTo(0, scrollY);
  }

  /** 更新卡片显示和分页按钮状态 */
  updateView() {
    var start = (this.currentPage - 1) * this.itemsPerPage;
    var end = start + this.itemsPerPage;

    /* 显示/隐藏卡片 */
    this.cards.forEach(function(card, i) {
      card.style.display = (i >= start && i < end) ? '' : 'none';
    });

    /* 更新分页按钮状态 */
    if (!this.paginationEl) return;

    var currentPage = this.currentPage;
    var totalPages = this.totalPages;

    /* 上一页按钮 */
    var prevBtn = this.paginationEl.querySelector('[data-page-prev]');
    if (prevBtn) prevBtn.disabled = currentPage <= 1;

    /* 下一页按钮 */
    var nextBtn = this.paginationEl.querySelector('[data-page-next]');
    if (nextBtn) nextBtn.disabled = currentPage >= totalPages;

    /* 页码按钮 */
    this.paginationEl.querySelectorAll('[data-page-num]').forEach(function(btn) {
      var pageNum = parseInt(btn.dataset.pageNum);
      if (pageNum === currentPage) {
        btn.classList.add('reviews-grid__page-btn--active');
      } else {
        btn.classList.remove('reviews-grid__page-btn--active');
      }
    });
  }

  /** 动态渲染分页按钮 */
  renderPagination() {
    if (!this.paginationEl || this.totalPages <= 1) return;

    var html = '';

    /* 上一页箭头 */
    html += '<button class="reviews-grid__page-btn" data-page-prev aria-label="Previous page">';
    html += '<svg width="12" height="12" viewBox="0 0 12 12" style="fill: none;"><path d="M7.5 9L4.5 6L7.5 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    html += '</button>';

    /* 页码按钮 */
    for (var i = 1; i <= this.totalPages; i++) {
      html += '<button class="reviews-grid__page-btn" data-page-num="' + i + '">' + i + '</button>';
    }

    /* 下一页箭头 */
    html += '<button class="reviews-grid__page-btn" data-page-next aria-label="Next page">';
    html += '<svg width="12" height="12" viewBox="0 0 12 12" style="fill: none;"><path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    html += '</button>';

    this.paginationEl.innerHTML = html;
  }

  /** 绑定分页事件 */
  bindEvents() {
    if (!this.paginationEl) return;
    var self = this;

    this.paginationEl.addEventListener('click', function(e) {
      var btn = e.target.closest('button');
      if (!btn) return;

      if (btn.hasAttribute('data-page-prev')) {
        self.goToPage(self.currentPage - 1);
      } else if (btn.hasAttribute('data-page-next')) {
        self.goToPage(self.currentPage + 1);
      } else if (btn.hasAttribute('data-page-num')) {
        self.goToPage(parseInt(btn.dataset.pageNum));
      }
    });
  }
}

/* ===== 5. 弹窗管理器 ===== */
class ReviewsModal {
  /**
   * @param {HTMLElement} overlayEl - 弹窗遮罩层元素
   */
  constructor(overlayEl) {
    this.overlay = overlayEl;
    this.titleEl = overlayEl.querySelector('[data-modal-title]');
    this.bodyEl = overlayEl.querySelector('[data-modal-body]');
    this.closeBtn = overlayEl.querySelector('[data-modal-close]');
    this.isOpen = false;
  }

  /** 初始化弹窗事件 */
  init() {
    var self = this;

    /* 关闭按钮 */
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function() { self.close(); });
    }

    /* 点击遮罩层关闭 */
    this.overlay.addEventListener('click', function(e) {
      if (e.target === self.overlay) self.close();
    });

    /* ESC 关闭 */
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
    });

    /* 绑定所有 VIEW DETAILS 按钮 */
    var gridEl = this.overlay.closest('[data-reviews-grid]');
    if (gridEl) {
      gridEl.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-article-modal]');
        if (!btn) return;

        var dataEl = btn.previousElementSibling;
        if (dataEl && dataEl.classList.contains('reviews-grid__article-data')) {
          try {
            var data = JSON.parse(dataEl.textContent);
            self.open(data.title, data.content);
          } catch (err) {
            console.error('解析文章数据失败:', err);
          }
        }
      });
    }
  }

  /**
   * 打开弹窗
   * @param {string} title - 标题
   * @param {string} content - HTML 内容
   */
  open(title, content) {
    if (this.titleEl) this.titleEl.textContent = title;
    if (this.bodyEl) this.bodyEl.innerHTML = content;
    this.overlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
  }

  /** 关闭弹窗 */
  close() {
    this.overlay.style.display = 'none';
    document.body.style.overflow = '';
    this.isOpen = false;
    if (this.bodyEl) this.bodyEl.innerHTML = '';
  }
}

/* ===== 初始化入口 ===== */
function initReviewsPage() {
  /* Tab 管理器 */
  var tabManager = new ReviewsTabManager();
  tabManager.init();

  /* 懒加载 fade-in 动画 */
  var lazyLoader = new ReviewsLazyLoader();
  lazyLoader.init();

  /* 视频懒加载 */
  var videoLoader = new ReviewsVideoLoader();
  videoLoader.init();

  /* Grid 分页器 + 弹窗 */
  document.querySelectorAll('[data-reviews-grid]').forEach(function(el) {
    var paginator = new ReviewsGridPaginator(el);
    paginator.init();

    var modalEl = el.querySelector('[data-grid-modal]');
    if (modalEl) {
      var modal = new ReviewsModal(modalEl);
      modal.init();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReviewsPage);
} else {
  initReviewsPage();
}
})();
